/**
 * Owner: Masa Bayu
 * File: AuthorPublicationsPanel.tsx
 * Date:Time: 2026-09-16 14:05
 * Description: T6-E2 author publications UI — PublishRequest timeline · no QXW.
 */

import { useId, useState } from 'react';
import {
  applyDemoAdminApprove,
  attemptDemoSelfApprove,
  createDemoPublishRequest,
  getDemoPublishRequest,
  getDemoPublishStatus,
  releaseDemoPublishLive,
  resetDemoAuthorPublish,
  submitDemoPublishRequest,
} from './demo-author-publish';
import {
  listSampleAccess,
  setSampleAccess,
  type SampleAccessRow,
} from './sample-access-store';
import { getHubSampleCeiling } from '../publication/hub-sample-ceiling';

export interface AuthorPublicationsPanelProps {
  readonly onNavigate: (path: string) => void;
}

function SampleAccessControls({ initial }: { readonly initial: SampleAccessRow }) {
  const [row, setRow] = useState(initial);
  const storePages = getHubSampleCeiling(row.publicationId);
  return (
    <li className="author-publications__sample-row">
      <span className="author-publications__sample-title">{row.title}</span>
      {storePages !== null ? (
        <span className="author-publications__sample-store">Store sample: {String(storePages)} pages</span>
      ) : null}
      <label className="author-publications__sample-toggle">
        <input
          type="checkbox"
          checked={row.enabled}
          onChange={(event) => {
            const next = setSampleAccess(row.publicationId, { enabled: event.target.checked });
            setRow({ ...row, ...next });
          }}
        />
        Read sample
      </label>
      <label className="author-publications__sample-pages">
        Up to page
        <input
          type="number"
          min={1}
          max={500}
          value={row.maxPage}
          disabled={!row.enabled}
          onChange={(event) => {
            const next = Number(event.target.value);
            if (!Number.isFinite(next)) {
              return;
            }
            const updated = setSampleAccess(row.publicationId, { maxPage: next });
            setRow({ ...row, ...updated });
          }}
        />
      </label>
    </li>
  );
}

export function AuthorPublicationsPanel({ onNavigate }: AuthorPublicationsPanelProps) {
  const statusId = useId();
  const controlsId = useId();
  const sampleListId = useId();
  const [status, setStatus] = useState(() => getDemoPublishStatus());
  const [request, setRequest] = useState(() => getDemoPublishRequest());
  const [sampleRows] = useState(() => listSampleAccess());

  const refresh = (nextStatus: string) => {
    setStatus(nextStatus);
    setRequest(getDemoPublishRequest());
  };

  return (
    <section className="route-panel author-publications" aria-labelledby="author-pubs-title">
      <h2 id="author-pubs-title">Author publications</h2>
      <p>
        PublishRequest status timeline — not a QXP editor. Admin approval required when policy
        requires (T7). QXWriter remains authoring authority.
      </p>

      <p id={statusId} role="status" aria-live="polite" className="author-publications__status">
        {status}
        {request !== null ? ` · timeline ${String(request.timeline.length)} events` : ''}
      </p>

      <div
        id={controlsId}
        role="group"
        aria-label="Author publishing actions"
        className="author-publications__controls"
      >
        <button
          type="button"
          onClick={() => {
            refresh(createDemoPublishRequest());
          }}
        >
          Create draft
        </button>
        <button
          type="button"
          onClick={() => {
            refresh(submitDemoPublishRequest(true));
          }}
        >
          Submit incomplete
        </button>
        <button
          type="button"
          onClick={() => {
            refresh(submitDemoPublishRequest(false));
          }}
        >
          Submit for review
        </button>
        <button
          type="button"
          onClick={() => {
            refresh(attemptDemoSelfApprove());
          }}
        >
          Attempt self-approve
        </button>
        <button
          type="button"
          onClick={() => {
            refresh(applyDemoAdminApprove());
          }}
        >
          Record Admin approve
        </button>
        <button
          type="button"
          onClick={() => {
            refresh(releaseDemoPublishLive());
          }}
        >
          Release live
        </button>
        <button
          type="button"
          onClick={() => {
            resetDemoAuthorPublish();
            refresh(getDemoPublishStatus());
          }}
        >
          Reset
        </button>
        <button
          type="button"
          onClick={() => {
            onNavigate('/author');
          }}
        >
          Dashboard
        </button>
      </div>

      {request !== null ? (
        <ol aria-label="Publish status timeline" className="author-publications__timeline">
          {request.timeline.map((event) => (
            <li key={`${event.kind}-${event.at}-${event.note ?? ''}`}>
              {event.kind}
              {event.note !== null ? ` — ${event.note}` : ''}
            </li>
          ))}
        </ol>
      ) : null}

      <section aria-labelledby={sampleListId} className="author-publications__sample">
        <h3 id={sampleListId}>Read Sample access</h3>
        <p>
          Buyers can turn as many pages as you set at Publish (Read sample pages). That store
          number is the ceiling. This list can only lower the sample in this browser — Publish
          again to raise it.
        </p>
        <ul className="author-publications__sample-list">
          {sampleRows.map((row) => (
            <SampleAccessControls key={row.publicationId} initial={row} />
          ))}
        </ul>
      </section>
    </section>
  );
}
